// src/components/DetalleIntervencionMercado.js
import React, { useEffect, useMemo, useState, useContext } from "react";
import { createPortal } from "react-dom";
import "./DetalleIntervencionMercado.css";

import mercadosService from "../services/mercadosService";
import { PARAM_CONFIG } from "../config/mercadoParametrosConfig";
import { MERCADO_DETALLE_LAYOUTS } from "../config/mercadoDetalleLayouts";
import { UserContext } from "../context/UserContext";

import CloseIcon from "@mui/icons-material/CloseRounded";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightIcon from "@mui/icons-material/ChevronRightRounded";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMapRounded";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibraryOutlined";

// ============================================================
// Gallery — galería de imágenes con preview lightbox,
// animación de swipe sutil y nav buttons con gradient overlay.
// ============================================================
const Gallery = ({ images, loading }) => {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0); // -1 prev, 1 next, 0 inicial
  const [showLightbox, setShowLightbox] = useState(false);

  useEffect(() => {
    setIndex(0);
    setDirection(0);
  }, [images]);

  const prev = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!images?.length) return;
    setDirection(-1);
    setIndex((i) => (i === 0 ? images.length - 1 : i - 1));
  };
  const next = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!images?.length) return;
    setDirection(1);
    setIndex((i) => (i === images.length - 1 ? 0 : i + 1));
  };
  const goTo = (i) => {
    setDirection(i > index ? 1 : -1);
    setIndex(i);
  };

  // Navegación con teclado en el lightbox
  useEffect(() => {
    if (!showLightbox) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "Escape") setShowLightbox(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLightbox, images?.length]);

  if (loading) {
    return (
      <div className="mdet-gallery mdet-gallery-empty">
        <div className="mdet-gallery-spinner" />
        <span>Cargando imágenes…</span>
      </div>
    );
  }
  if (!images || images.length === 0) {
    return (
      <div className="mdet-gallery mdet-gallery-empty">
        <PhotoLibraryIcon style={{ fontSize: 32, opacity: 0.55 }} />
        <span>Sin imágenes disponibles</span>
      </div>
    );
  }

  const dirClass = direction === -1 ? "dir-prev" : direction === 1 ? "dir-next" : "dir-init";

  return (
    <div className="mdet-gallery">
      <div
        className="mdet-gallery-main"
        onClick={() => setShowLightbox(true)}
        role="button"
        tabIndex={0}
      >
        <img
          key={`${images[index]}-${index}`}
          src={images[index]}
          alt={`Imagen ${index + 1}`}
          className={`mdet-gallery-img ${dirClass}`}
          draggable={false}
        />

        {images.length > 1 && (
          <>
            <button
              type="button"
              className="mdet-gallery-nav left"
              onClick={prev}
              aria-label="Imagen anterior"
            >
              <ChevronLeftIcon style={{ fontSize: 28 }} />
            </button>
            <button
              type="button"
              className="mdet-gallery-nav right"
              onClick={next}
              aria-label="Siguiente imagen"
            >
              <ChevronRightIcon style={{ fontSize: 28 }} />
            </button>
          </>
        )}

        <div className="mdet-gallery-counter">
          {index + 1} / {images.length}
        </div>
        <div className="mdet-gallery-zoomhint">
          <ZoomOutMapIcon style={{ fontSize: 14 }} />
          Click para ampliar
        </div>
      </div>

      {images.length > 1 && (
        <div className="mdet-gallery-thumbs">
          {images.map((src, i) => (
            <button
              key={`${src}-${i}`}
              type="button"
              className={`mdet-gallery-thumb ${i === index ? "is-active" : ""}`}
              onClick={() => goTo(i)}
              aria-label={`Ir a imagen ${i + 1}`}
            >
              <img src={src} alt="" draggable={false} />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox preview (portalizado a document.body) */}
      {showLightbox &&
        createPortal(
          <div
            className="mdet-lightbox"
            onClick={() => setShowLightbox(false)}
          >
            <img
              key={`lb-${images[index]}-${index}`}
              src={images[index]}
              alt={`Imagen ${index + 1}`}
              className={`mdet-lightbox-img ${dirClass}`}
              onClick={(e) => e.stopPropagation()}
              draggable={false}
            />

            <button
              type="button"
              className="mdet-lightbox-close"
              onClick={(e) => {
                e.stopPropagation();
                setShowLightbox(false);
              }}
              aria-label="Cerrar vista previa"
            >
              <CloseIcon style={{ fontSize: 24 }} />
            </button>

            {images.length > 1 && (
              <>
                <button
                  type="button"
                  className="mdet-lightbox-nav left"
                  onClick={prev}
                  aria-label="Imagen anterior"
                >
                  <ChevronLeftIcon style={{ fontSize: 40 }} />
                </button>
                <button
                  type="button"
                  className="mdet-lightbox-nav right"
                  onClick={next}
                  aria-label="Siguiente imagen"
                >
                  <ChevronRightIcon style={{ fontSize: 40 }} />
                </button>
              </>
            )}

            <div className="mdet-lightbox-counter">
              {index + 1} / {images.length}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

// ============================================================
// DetalleField — par label/value en el grid
// ============================================================
const DetalleField = ({ label, value, full }) => (
  <div className={`mdet-field ${full ? "is-full" : ""}`}>
    <span className="mdet-field-label">{label}</span>
    <span className="mdet-field-value">{value || "—"}</span>
  </div>
);

// Keys de PARAM_CONFIG que típicamente contienen texto largo y por ende
// se renderizan ocupando todo el ancho del grid en su propia fila.
const FULL_WIDTH_KEYS = new Set([
  "objetoConvenio",     // OBJETO del convenio
  "desarrolloEjec",     // EJECUCION ACTUAL
  "otrosEjec",          // OTROS (ejecución)
  "otrosDesc",          // OTROS (descripción del proyecto)
  "mercadoContingencia",
]);

// ============================================================
// Componente principal
// ============================================================
const DetalleIntervencionMercado = ({
  idIntervencion,
  id_estado_intervencion,
  id_modalidad,
  headerStyle: propHeaderStyle,
  onClose,
  fechaActualizacion,
}) => {
  const { authenticated, refreshSession } = useContext(UserContext);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [imagenes, setImagenes] = useState([]);
  const [loadingImg, setLoadingImg] = useState(false);

  const [pdfs, setPdfs] = useState([]);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [loadingPdfs, setLoadingPdfs] = useState(false);

  const [activeTab, setActiveTab] = useState("general");

  // Elegir layout
  const layout = useMemo(() => {
    const estado = Number(id_estado_intervencion);
    const mod = Number(id_modalidad);
    if (estado === 1) return MERCADO_DETALLE_LAYOUTS.EJECUTADA;
    if (estado === 2) return MERCADO_DETALLE_LAYOUTS.EN_EJECUCION;
    if (estado === 3 || estado === 4) return MERCADO_DETALLE_LAYOUTS.EXP_TEC;
    if (estado === 6) return MERCADO_DETALLE_LAYOUTS.PREINVERSION;
    if (estado === 7) {
      if (mod === 14) return MERCADO_DETALLE_LAYOUTS.EXP_TEC;
      if (mod === 15) return MERCADO_DETALLE_LAYOUTS.PREINVERSION;
    }
    return MERCADO_DETALLE_LAYOUTS.DEFAULT;
  }, [id_estado_intervencion, id_modalidad]);

  // Cargar parámetros
  useEffect(() => {
    const run = async () => {
      if (!idIntervencion) return;
      setLoading(true);
      setError(null);
      try {
        const resp = await mercadosService.getMercadoAdminDetalle(idIntervencion);
        const arr = Array.isArray(resp) ? resp : [];
        setRows(arr);
      } catch (e) {
        console.error(e);
        setError("No se pudo cargar el detalle de la intervención.");
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [idIntervencion]);

  // Cargar imágenes
  useEffect(() => {
    const runImg = async () => {
      if (!idIntervencion) return;
      setLoadingImg(true);
      try {
        const resp = await mercadosService.getMercadoAdminImagenes(idIntervencion);
        const items = Array.isArray(resp) ? resp : resp?.items ?? [];
        const urls = items.map((x) => x.url || x.ruta || x.path || x.fileUrl).filter(Boolean);
        setImagenes(urls);
      } catch (e) {
        console.error(e);
        setImagenes([]);
      } finally {
        setLoadingImg(false);
      }
    };
    runImg();
  }, [idIntervencion]);

  // Cargar PDFs sólo si está autenticado
  useEffect(() => {
    if (!idIntervencion || !authenticated) return;
    const runPdfs = async () => {
      setLoadingPdfs(true);
      try {
        const resp = await mercadosService.getMercadoAdminPdfs(idIntervencion);
        const items = Array.isArray(resp) ? resp : resp?.items ?? [];
        setPdfs(items);
        // Auto-selecciona el último/marcado como vigente para preview
        const ultimo =
          items.find((x) => x.esUltimo === true || x.es_ultimo === true) ||
          items[0];
        setSelectedPdf(ultimo || null);
      } catch (e) {
        console.error(e);
        setPdfs([]);
        setSelectedPdf(null);
      } finally {
        setLoadingPdfs(false);
      }
    };
    runPdfs();
  }, [idIntervencion, authenticated]);

  // Si el usuario pierde autenticación, vuelve al tab general
  useEffect(() => {
    if (!authenticated && activeTab !== "general") setActiveTab("general");
  }, [authenticated, activeTab]);

  // ==========================
  // Helpers
  // ==========================
  const getValById = (idInfra) => {
    const encontrado = [...(rows || [])]
      .reverse()
      .find(
        (p) => Number(p.idInfraestructura ?? p.id_infraestructura) === Number(idInfra)
      );
    if (!encontrado) return "";
    return (encontrado?.valorDescripcion ?? encontrado?.valor_descripcion ?? "")
      .toString()
      .trim();
  };

  // Mapea una key del PARAM_CONFIG a su valor — necesario para iterar
  // los detallesClick del layout, que vienen referenciados por key textual.
  const getVal = (key) => {
    const meta = PARAM_CONFIG?.[key];
    if (!meta) return "";
    return getValById(meta.idInfraestructura);
  };

  const money = (v) => {
    if (!v) return "";
    const n = Number(String(v).replace(/,/g, "").replace(/S\/\./gi, "").trim());
    if (Number.isNaN(n)) return v;
    return `S/. ${n.toLocaleString("es-PE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const upper = (v) => (v ? String(v).toUpperCase() : "");

  // ==========================
  // Datos
  // ==========================
  const nombre = upper(getValById(61));
  const dep = upper(getValById(67));
  const prov = upper(getValById(66));
  const dist = upper(getValById(65));
  const ubicacion = [dep, prov, dist].filter(Boolean).join(" · ");

  const ubigeo = getValById(138);
  const tipo = upper(getValById(80));
  const cui = getValById(70);
  const puestos = getValById(75);
  const costoInv = money(getValById(73));
  const beneficiarios = getValById(74);

  const uf = upper(getValById(92));
  const uei = upper(getValById(97));

  const estadoGeneral =
    upper(getValById(136)) ||
    upper(getValById(129)) ||
    upper(getValById(122)) ||
    upper(getValById(106)) ||
    upper(getValById(94)) ||
    upper(getValById(82)) ||
    "";

  const lat = getValById(68);
  const lon = getValById(69);
  const coords =
    lat || lon ? `${lat || ""}${lat && lon ? ", " : ""}${lon || ""}` : "";

  const caracteristicas = getValById(72);
  const entregaObra = getValById(128);
  const inicioObra = getValById(123);
  const montoContr = money(getValById(116));
  const plazoEjec = getValById(125);
  const registroCierre = getValById(149);
  const modalidad = getValById(63);
  const fechaViab = getValById(96);

  const ejecPor =
    Number(id_estado_intervencion) === 7
      ? Number(id_modalidad) === 14
        ? upper(getValById(159))
        : upper(getValById(161))
      : upper(modalidad);

  const fechaActualiz = (getValById(162) || fechaActualizacion || "").trim();

  // Etiqueta / pill de etapa
  const etapaLabel = useMemo(() => {
    const E = Number(id_estado_intervencion);
    const M = Number(id_modalidad);
    if (E === 1) return "Obra ejecutada";
    if (E === 2) return "Obra en ejecución";
    if (E === 3 || E === 4 || (E === 7 && M === 14)) return "Expediente técnico";
    if (E === 6 || (E === 7 && M === 15)) return "Preinversión";
    return "Intervención";
  }, [id_estado_intervencion, id_modalidad]);

  // Fields por layout
  const fields = useMemo(() => {
    const E = Number(id_estado_intervencion);
    const M = Number(id_modalidad);
    const base = [
      { label: "Ubicación", value: ubicacion },
      { label: "Ubigeo", value: ubigeo },
      { label: "Tipo", value: tipo },
      { label: "CUI", value: cui },
    ];

    if (E === 1) {
      return [
        { label: "Ejecutada por", value: ejecPor },
        ...base,
        { label: "Entrega de obra", value: entregaObra },
        { label: "N° puestos", value: puestos },
        { label: "Costo inversión", value: costoInv },
        { label: "Beneficiarios", value: beneficiarios },
        { label: "Unidad formuladora", value: uf },
        { label: "Unidad ejecutora", value: uei },
        { label: "Estado", value: estadoGeneral },
        { label: "Coordenadas", value: coords },
        { label: "Registro de cierre", value: registroCierre },
      ];
    }
    if (E === 2) {
      return [
        { label: "Ejecutada por", value: ejecPor },
        ...base,
        { label: "Inicio de obra", value: inicioObra },
        { label: "N° puestos", value: puestos },
        { label: "Monto contractual", value: montoContr },
        { label: "Costo inversión", value: costoInv },
        { label: "Beneficiarios", value: beneficiarios },
        { label: "Unidad formuladora", value: uf },
        { label: "Unidad ejecutora", value: uei },
        { label: "Plazo de ejecución", value: plazoEjec },
        { label: "Estado", value: estadoGeneral },
        { label: "Coordenadas", value: coords },
      ];
    }
    if (E === 3 || E === 4 || (E === 7 && M === 14)) {
      return [
        { label: "Elaborado por", value: ejecPor },
        ...base,
        { label: "N° puestos", value: puestos },
        { label: "Costo inversión", value: costoInv },
        { label: "Beneficiarios", value: beneficiarios },
        { label: "Unidad formuladora", value: uf },
        { label: "Unidad ejecutora", value: uei },
        { label: "Estado", value: estadoGeneral },
        { label: "Coordenadas", value: coords },
        { label: "Modalidad", value: modalidad },
      ];
    }
    if (E === 6 || (E === 7 && M === 15)) {
      return [
        { label: "Elaborado por", value: ejecPor },
        ...base,
        { label: "N° puestos", value: puestos },
        { label: "Costo inversión", value: costoInv },
        { label: "Beneficiarios", value: beneficiarios },
        { label: "Unidad formuladora", value: uf },
        { label: "Unidad ejecutora", value: uei },
        { label: "Modalidad", value: modalidad },
        { label: "Fecha de viabilidad", value: fechaViab },
        { label: "Coordenadas", value: coords },
      ];
    }
    return [...base, { label: "Coordenadas", value: coords }];
  }, [
    id_estado_intervencion,
    id_modalidad,
    ubicacion,
    ubigeo,
    tipo,
    cui,
    ejecPor,
    entregaObra,
    inicioObra,
    puestos,
    montoContr,
    costoInv,
    beneficiarios,
    uf,
    uei,
    plazoEjec,
    estadoGeneral,
    coords,
    registroCierre,
    modalidad,
    fechaViab,
  ]);

  const handleClose = () => {
    if (typeof onClose === "function") onClose();
  };

  const handleDownloadAyudaMemoria = async () => {
    try {
      setLoadingPdfs(true);
      const isStillAuth = await refreshSession();
      if (!isStillAuth) {
        alert("Su sesión ha expirado. Por favor, vuelva a iniciar sesión.");
        return;
      }
      const resp = await mercadosService.getMercadoAdminPdfs(idIntervencion);
      const items = Array.isArray(resp) ? resp : resp?.items ?? [];
      const ultimo = items.find((x) => x.esUltimo === true || x.es_ultimo === true);
      if (ultimo?.url) {
        window.open(ultimo.url, "_blank");
      } else {
        alert("No se encontró un documento marcado como 'Ayuda Memoria' (último).");
      }
    } catch (e) {
      console.error(e);
      alert("Error al intentar obtener el documento.");
    } finally {
      setLoadingPdfs(false);
    }
  };

  // ==========================
  // Render
  // ==========================
  if (!layout) {
    return (
      <div className="mdet-card">
        <div className="mdet-card-empty">
          No hay layout para este tipo de intervención.
        </div>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="mdet-card">
        <div className="mdet-card-empty">
          <div className="mdet-gallery-spinner" />
          Cargando detalle…
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="mdet-card">
        <div className="mdet-card-empty">{error}</div>
      </div>
    );
  }

  // Color del header: si el padre lo pasa (basado en estado/modalidad) lo
  // usamos como acento sutil; si no, fallback a un slate neutro.
  const headerAccent =
    propHeaderStyle?.backgroundColor || "var(--produce-red, #DC2626)";

  return (
    <div className="mdet-card" style={{ "--accent": headerAccent }}>
      {/* HEADER sticky glass */}
      <header className="mdet-card-head">
        <div className="mdet-card-head-info">
          <span className="mdet-card-pill">{etapaLabel}</span>
          <h2 className="mdet-card-title">{nombre || "Sin nombre"}</h2>
          {fechaActualiz && (
            <span className="mdet-card-meta">
              Actualizado · {fechaActualiz}
            </span>
          )}
        </div>
        <button
          type="button"
          className="mdet-card-close"
          onClick={handleClose}
          aria-label="Cerrar"
          title="Cerrar"
        >
          <CloseIcon style={{ fontSize: 20 }} />
        </button>
      </header>

      {/* TABS BAR — sólo visible cuando hay info adicional (autenticado) */}
      {authenticated && (
        <nav className="mdet-tabs">
          <button
            type="button"
            className={`mdet-tab ${activeTab === "general" ? "is-active" : ""}`}
            onClick={() => setActiveTab("general")}
          >
            General
          </button>
          {(layout.detallesClick || []).map((d) => (
            <button
              key={d.key}
              type="button"
              className={`mdet-tab ${activeTab === d.key ? "is-active" : ""}`}
              onClick={() => setActiveTab(d.key)}
            >
              {d.label
                ? d.label.charAt(0) + d.label.slice(1).toLowerCase()
                : d.key}
            </button>
          ))}
          <button
            type="button"
            className={`mdet-tab ${activeTab === "documentos" ? "is-active" : ""}`}
            onClick={() => setActiveTab("documentos")}
          >
            Documentos
            {pdfs.length > 0 && (
              <span className="mdet-tab-count">{pdfs.length}</span>
            )}
          </button>
        </nav>
      )}

      {/* BODY scrollable — salvo en la tab Documentos, donde el scroll
          lo maneja exclusivamente el iframe del PDF para no tener dos
          barras de scroll a la vez. */}
      <div
        className={`mdet-card-body ${
          activeTab === "documentos" ? "is-docs" : ""
        }`}
      >
        {activeTab === "general" && (
          <>
            <section className="mdet-card-section">
              <div className="mdet-data-grid">
                {fields.map((f) => (
                  <DetalleField key={f.label} label={f.label} value={f.value} />
                ))}
              </div>
            </section>

            {caracteristicas && (
              <section className="mdet-card-section">
                <h3 className="mdet-section-title">
                  Características del proyecto
                </h3>
                <p className="mdet-card-text">{caracteristicas}</p>
              </section>
            )}

            <section className="mdet-card-section">
              <h3 className="mdet-section-title">Galería</h3>
              <Gallery images={imagenes} loading={loadingImg} />
            </section>
          </>
        )}

        {/* Tabs de detallesClick (CONVENIO, DESC_PROY, EJEC_FISICA, etc.) */}
        {authenticated &&
          (layout.detallesClick || []).map((d) => {
            if (activeTab !== d.key) return null;
            return (
              <React.Fragment key={d.key}>
                {(d.secciones || []).map((sec, si) => (
                  <section key={si} className="mdet-card-section">
                    {sec.titulo && (
                      <h3 className="mdet-section-title">{sec.titulo}</h3>
                    )}
                    <div className="mdet-data-grid">
                      {(sec.filas || []).map((row) => (
                        <DetalleField
                          key={row.label}
                          label={row.label}
                          value={getVal(row.key)}
                          full={FULL_WIDTH_KEYS.has(row.key)}
                        />
                      ))}
                    </div>
                  </section>
                ))}
              </React.Fragment>
            );
          })}

        {/* Tab Documentos */}
        {authenticated && activeTab === "documentos" && (
          <section className="mdet-card-section mdet-docs">
            {loadingPdfs ? (
              <div className="mdet-card-empty" style={{ padding: "40px 20px" }}>
                <div className="mdet-gallery-spinner" />
                Cargando documentos…
              </div>
            ) : pdfs.length === 0 ? (
              <div className="mdet-card-empty" style={{ padding: "40px 20px" }}>
                Sin documentos disponibles
              </div>
            ) : (
              <div className="mdet-docs-layout">
                {/* Lista lateral de PDFs */}
                <ul className="mdet-docs-list">
                  {pdfs.map((p) => {
                    const isActive = selectedPdf?.id === p.id;
                    const fecha =
                      p.fecha ||
                      p.fechaCreacion ||
                      p.fecha_subida ||
                      p.fechaSubida;
                    return (
                      <li key={p.id}>
                        <button
                          type="button"
                          className={`mdet-doc-item ${isActive ? "is-active" : ""}`}
                          onClick={() => setSelectedPdf(p)}
                        >
                          <span className="mdet-doc-icon">PDF</span>
                          <div className="mdet-doc-info">
                            <span className="mdet-doc-name">
                              {p.nombre || p.titulo || p.fileName || `Documento ${p.id}`}
                            </span>
                            {fecha && (
                              <span className="mdet-doc-meta">{fecha}</span>
                            )}
                          </div>
                          {(p.esUltimo || p.es_ultimo) && (
                            <span className="mdet-doc-pill">Vigente</span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>

                {/* Preview + acciones */}
                <div className="mdet-docs-preview">
                  {selectedPdf?.url ? (
                    <>
                      <div className="mdet-docs-toolbar">
                        <span className="mdet-docs-toolbar-name">
                          {selectedPdf.nombre ||
                            selectedPdf.titulo ||
                            selectedPdf.fileName ||
                            "Documento"}
                        </span>
                        <a
                          className="mdet-action-btn"
                          href={selectedPdf.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          <DownloadIcon style={{ fontSize: 18 }} />
                          Descargar
                        </a>
                      </div>
                      <iframe
                        key={selectedPdf.id}
                        src={selectedPdf.url}
                        title={selectedPdf.nombre || "Documento PDF"}
                        className="mdet-docs-iframe"
                      />
                    </>
                  ) : (
                    <div className="mdet-docs-empty">
                      Selecciona un documento de la lista
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default DetalleIntervencionMercado;
